import {
  Goal,
  GoalGroupBy,
  GoalReorderSectionUpdate,
  GoalRoadmapSortingKeys,
  goalsArchivedSectionID,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
  rootId,
  UpdateGoal,
  UpdateLifeArea,
  UserSettings,
} from '@griply/types';

import { getSubGoals } from './goals/get-sub-goals';

export type BuildGoalOverviewReorderUpdateOptions = {
  updatedSections: GoalReorderSectionUpdate[];
  updatedGoals: UpdateGoal[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  groupBy: GoalGroupBy;
  /** Premium gate — de-archiving on reorder is only applied when allowed. */
  allowed: boolean;
  currentGoalGroupSorting?: Record<ID, ID[]>;
};

export type GoalOverviewReorderUpdate = {
  goalUpdates: UpdateGoal[];
  lifeAreaUpdates: UpdateLifeArea[];
  settingsUpdate: Partial<UserSettings>;
};

export const buildGoalOverviewReorderUpdate = ({
  updatedSections,
  updatedGoals,
  goals,
  lifeAreas,
  groupBy,
  allowed,
  currentGoalGroupSorting,
}: BuildGoalOverviewReorderUpdateOptions): GoalOverviewReorderUpdate => {
  const isLifeAreaGrouping = groupBy === GroupByMode.LifeArea;

  const settingsUpdate: Partial<UserSettings> = {
    goalSortingMode: GoalSortingMode.Custom,
    filters: { goalsOverview: { sortingMode: GoalSortingMode.Custom } },
  };

  const goalsMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
    acc[goal.id] = goal;
    return acc;
  }, {});

  const lifeAreaMap = lifeAreas.reduce<Record<ID, LifeArea>>(
    (acc, lifeArea) => {
      acc[lifeArea.id] = lifeArea;
      return acc;
    },
    {},
  );

  const goalUpdateMap = new Map<ID, UpdateGoal>(
    updatedGoals.map((goal) => [goal.id, { ...goal }]),
  );
  const mergeGoalUpdate = (update: UpdateGoal) => {
    goalUpdateMap.set(update.id, {
      ...goalUpdateMap.get(update.id),
      ...update,
    });
  };

  const lifeAreaUpdates: UpdateLifeArea[] = [];

  const isArchivedSectionUpdated = updatedSections.some(
    ({ id }) => id === goalsArchivedSectionID,
  );

  const archiveGoal = (goalId: ID, archivedAt: Date | null) => {
    const existing = goalUpdateMap.get(goalId);
    if (existing) {
      existing.archivedAt = archivedAt;
      return;
    }
    mergeGoalUpdate({ id: goalId, archivedAt });
  };

  updatedSections.forEach(({ id, goalSorting }) => {
    if (id !== goalsArchivedSectionID && isArchivedSectionUpdated) {
      goalSorting.forEach((goalId) => {
        const goal = goalsMap[goalId];
        if (!goal || !goal.archivedAt || !allowed) {
          return;
        }
        archiveGoal(goalId, null);
      });
    }

    if (id === rootId) {
      if (isLifeAreaGrouping) {
        settingsUpdate.lifeAreaSorting = goalSorting;
        return;
      }
      settingsUpdate.goalRoadmapSorting = {
        [GoalRoadmapSortingKeys.InProgress]: goalSorting,
      };
      return;
    }

    if (id === goalsArchivedSectionID) {
      goalSorting
        .filter((goalId) => goalsMap[goalId])
        .forEach((goalId) => {
          const goal = goalsMap[goalId];
          if (!goal) {
            return;
          }
          if (!goal.archivedAt) {
            archiveGoal(goalId, new Date());
          }

          getSubGoals(goalId, { goals, recursive: true }).forEach((subGoal) => {
            if (!subGoal.archivedAt) {
              archiveGoal(subGoal.id, new Date());
            }
          });
        });
      return;
    }

    const lifeArea = lifeAreaMap[id];
    if (lifeArea) {
      lifeAreaUpdates.push({ id, goalSorting });
      return;
    }

    settingsUpdate.goalGroupSorting = {
      lifeArea: {
        sorting: {
          ...currentGoalGroupSorting,
          ...settingsUpdate.goalGroupSorting?.lifeArea?.sorting,
          [id === lifeAreaOtherID ? 'null' : id]: goalSorting,
        },
      },
    };
  });

  const goalUpdates = Array.from(goalUpdateMap.values()).map((goal) => ({
    ...goal,
    goalSortingMode: GoalSortingMode.Custom,
  }));

  return { goalUpdates, lifeAreaUpdates, settingsUpdate };
};
